import React, { useEffect, useState, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Legend } from "recharts";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function LabTrendChart({ patientId, analyte, onClose }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/patients/${patientId}/exams/trend`, { params: { analyte } })
            .then((r) => setData(r.data.series || []))
            .finally(() => setLoading(false));
    }, [patientId, analyte]);

    const chartData = useMemo(
        () => data
            .filter((d) => d.numeric != null)
            .map((d) => ({ ...d, dateLabel: (d.date || "").slice(0, 10) })),
        [data]
    );

    const unit = chartData[0]?.unit || "";
    const referenceRange = chartData[chartData.length - 1]?.reference_range;
    let refLow, refHigh;
    if (referenceRange) {
        const match = String(referenceRange).match(/([\d.,]+)\s*[-–a]\s*([\d.,]+)/);
        if (match) {
            refLow = parseFloat(match[1].replace(",", "."));
            refHigh = parseFloat(match[2].replace(",", "."));
        }
    }

    return (
        <div className="card-earthy" data-testid="lab-trend-chart">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg text-brand">
                    Tendência: <span className="font-medium">{analyte}</span>
                    {unit && <span className="text-sm text-muted-clinical ml-1">({unit})</span>}
                </h3>
                {onClose && (
                    <button onClick={onClose} className="btn-ghost text-xs" data-testid="lab-trend-close">Fechar</button>
                )}
            </div>
            {loading ? (
                <div className="h-56 flex items-center justify-center"><Loader2 className="animate-spin text-brand" /></div>
            ) : chartData.length === 0 ? (
                <p className="text-sm text-muted-clinical italic">Sem valores numéricos registrados para {analyte}.</p>
            ) : (
                <div className="h-64">
                    <ResponsiveContainer>
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,63,51,0.1)" />
                            <XAxis dataKey="dateLabel" style={{ fontSize: 12 }} />
                            <YAxis style={{ fontSize: 12 }} />
                            <Tooltip
                                contentStyle={{ background: "#FFF", border: "1px solid #ccc", borderRadius: 6 }}
                                formatter={(v, k, item) => [`${v} ${item.payload.unit || ""}${item.payload.flag ? ` (${item.payload.flag})` : ""}`, analyte]}
                            />
                            {refLow != null && <ReferenceLine y={refLow} stroke="#8A6A15" strokeDasharray="4 4" label={{ value: `min ${refLow}`, position: "insideBottomLeft", fontSize: 10, fill: "#8A6A15" }} />}
                            {refHigh != null && <ReferenceLine y={refHigh} stroke="#8A6A15" strokeDasharray="4 4" label={{ value: `max ${refHigh}`, position: "insideTopLeft", fontSize: 10, fill: "#8A6A15" }} />}
                            <Line
                                type="monotone"
                                dataKey="numeric"
                                stroke="#1E3F33"
                                strokeWidth={2}
                                dot={{ r: 4, fill: "#1E3F33" }}
                                activeDot={{ r: 6 }}
                                name={analyte}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
            {referenceRange && (
                <p className="text-xs text-muted-clinical mt-2">
                    Faixa de referência mais recente: {referenceRange}
                </p>
            )}
        </div>
    );
}
